/**
 * MockTransport.ts — a complete standalone backend.
 *
 * Simulates the Go server fully from math/config.json so ColorDrop RUNS with no
 * server. It is authoritative: it owns balance, pre-rolls outcomes on buyColor,
 * and streams cell_reveal / color_complete / picture_complete / fomo / balance
 * frames over a fake WebSocket in response to tap_cell frames.
 */
import { Rng, pseudoHash, randomHex } from '../util/rng';
import {
  BET_LEVELS,
  MATH_VERSION,
  SPLIT,
  buildMathConfig,
} from './mathConfig';
import {
  rollColorOutcome,
  rollPictureOutcome,
  projectedTeaser,
  type ColorOutcome,
} from './mockMath';
import { getPictureById, getPictureLibrary } from '../pictures/library';
import type { PictureDef } from '../pictures/types';
import type {
  AutoplayRequest,
  AutoplayResponse,
  AutoplayStop,
  AutoplayStopRequest,
  BuyRequest,
  BuyResponse,
  CellDef,
  ClientWsMessage,
  FairnessResponse,
  PaletteEntry,
  PictureInstanceResponse,
  PictureSummary,
  PicturesResponse,
  RotateSeedResponse,
  ServerWsMessage,
  SessionRequest,
  SessionResponse,
} from './contract';
import type { Transport } from './Transport';

interface ActiveBet {
  betId: string;
  colorId: number;
  stake: number;
  cellCount: number;
  outcome: ColorOutcome;
  revealedCursor: number; // cells revealed so far
  cellIndexToOrder: number[]; // map cellIndex -> reveal order slot
  done: boolean;
}

interface PictureInstance {
  pictureInstanceId: string;
  def: PictureDef;
  K: number;
  cellsByColor: Map<number, number[]>; // colorId -> cellIds
  boughtColors: Set<number>;
  completedColors: Set<number>;
  totalStakeAcc: number;
  pictureResolved: boolean;
}

const START_BALANCE = 1000;

export class MockTransport implements Transport {
  /**
   * The MockTransport is a FUN-mode client-authoritative simulator. It must NEVER ship in a real-
   * money build (an iGaming audit blocker — a misconfig'd .env would otherwise turn the client
   * into its own bank). The constructor refuses to instantiate when:
   *   - import.meta.env.PROD is true AND VITE_REAL_MONEY=1, or
   *   - VITE_FORBID_MOCK=1 is explicitly set by the operator at build time.
   * Dev/demo builds (VITE_BACKEND=mock, the default for `npm run dev`) work as before.
   */
  static guard(): void {
    const env = (import.meta as unknown as { env?: Record<string, string | boolean | undefined> }).env ?? {};
    if (env.VITE_FORBID_MOCK === '1' || env.VITE_FORBID_MOCK === 'true') {
      throw new Error('MockTransport disabled by build (VITE_FORBID_MOCK=1)');
    }
    if (env.PROD === true && (env.VITE_REAL_MONEY === '1' || env.VITE_REAL_MONEY === 'true')) {
      throw new Error('MockTransport refuses to run in a PROD real-money build');
    }
  }

  private serverSeed = randomHex(64);
  private serverSeedHash = pseudoHash(this.serverSeed);
  private clientSeed = randomHex(16);
  private nonce = 0;
  private balance = START_BALANCE;
  private currency = 'USD';
  private sessionId = 'mock-' + randomHex(8);
  readonly kind = 'mock' as const;

  constructor() {
    MockTransport.guard();
  }

  private listeners = new Set<(m: ServerWsMessage) => void>();
  private bets = new Map<string, ActiveBet>();
  private currentPicture: PictureInstance | null = null;
  private idempotency = new Map<string, BuyResponse>();

  private autoplayTimer: ReturnType<typeof setTimeout> | null = null;
  private autoplayRunId: string | null = null;

  // ---- helpers ----
  private emit(m: ServerWsMessage): void {
    // deliver async to mimic network
    queueMicrotask(() => this.listeners.forEach((l) => l(m)));
  }
  private get seedStr(): string {
    return `${this.serverSeed}:${this.clientSeed}`;
  }

  // ---- REST ----
  async createSession(req: SessionRequest): Promise<SessionResponse> {
    if (req.clientSeed) this.clientSeed = req.clientSeed;
    this.currency = req.currency || 'USD';
    return {
      sessionId: this.sessionId,
      serverSeedHash: this.serverSeedHash,
      clientSeed: this.clientSeed,
      nonce: this.nonce,
      mathVersion: MATH_VERSION,
      mathConfig: buildMathConfig(),
      balance: this.balance,
      currency: this.currency,
    };
  }

  async getPictures(): Promise<PicturesResponse> {
    const lib = getPictureLibrary();
    const pictures: PictureSummary[] = lib.map((def) => {
      const counts = new Map<number, number>();
      for (const c of def.cells) counts.set(c.colorId, (counts.get(c.colorId) ?? 0) + 1);
      const palette: PaletteEntry[] = def.palette.map((p) => ({
        colorId: p.colorId,
        number: p.number,
        hex: p.hex,
        cellCount: counts.get(p.colorId) ?? 0,
      }));
      const totalCells = def.cells.length;
      // recommended stakes: a comfortable trio from bet levels by size
      const stakeIdx =
        def.sizeName === 'large' ? [3, 5, 6] : def.sizeName === 'medium' ? [2, 3, 4] : [1, 2, 3];
      return {
        pictureId: def.id,
        title: def.title,
        thumbnailUrl: '',
        palette,
        totalCells,
        recommendedStakes: stakeIdx.map((i) => BET_LEVELS[i]),
        sizeName: def.sizeName,
        tags: def.tags ?? [],
        sourceImage: def.sourceImage,
      };
    });
    return { pictures };
  }

  async selectPicture(pictureId: string): Promise<PictureInstanceResponse> {
    const def = getPictureById(pictureId);
    if (!def) throw new Error('unknown picture');
    const cellsByColor = new Map<number, number[]>();
    for (const c of def.cells) {
      if (!cellsByColor.has(c.colorId)) cellsByColor.set(c.colorId, []);
      cellsByColor.get(c.colorId)!.push(c.cellId);
    }
    const instanceId = 'pi-' + randomHex(8);
    this.currentPicture = {
      pictureInstanceId: instanceId,
      def,
      K: def.palette.length,
      cellsByColor,
      boughtColors: new Set(),
      completedColors: new Set(),
      totalStakeAcc: 0,
      pictureResolved: false,
    };
    this.bets.clear();

    const counts = new Map<number, number>();
    for (const c of def.cells) counts.set(c.colorId, (counts.get(c.colorId) ?? 0) + 1);
    const palette: PaletteEntry[] = def.palette.map((p) => ({
      colorId: p.colorId,
      number: p.number,
      hex: p.hex,
      cellCount: counts.get(p.colorId) ?? 0,
    }));
    const cells: CellDef[] = def.cells.map((c) => ({
      cellId: c.cellId,
      colorId: c.colorId,
      cx: c.cx,
      cy: c.cy,
      labelR: c.labelR,
      poly: c.poly,
    }));
    return { pictureInstanceId: instanceId, palette, cells };
  }

  async buyColor(req: BuyRequest): Promise<BuyResponse> {
    if (this.idempotency.has(req.idempotencyKey)) {
      return this.idempotency.get(req.idempotencyKey)!;
    }
    const pic = this.currentPicture;
    if (!pic) throw new Error('no picture selected');
    if (pic.boughtColors.has(req.colorId)) throw new Error('color already bought');
    if (this.balance < req.stake) throw new Error('insufficient balance');

    const cellIds = pic.cellsByColor.get(req.colorId) ?? [];
    const cellCount = cellIds.length;
    this.nonce++;
    const betId = 'bet-' + randomHex(8);

    // debit
    this.balance = +(this.balance - req.stake).toFixed(2);
    pic.boughtColors.add(req.colorId);
    pic.totalStakeAcc = +(pic.totalStakeAcc + req.stake).toFixed(2);

    // pre-roll outcome (authoritative)
    const outcome = rollColorOutcome(this.seedStr, betId, req.stake, cellCount);

    // map cellId -> reveal order: reveal in the cell order returned to client
    const cellIndexToOrder = cellIds.map((_, i) => i);

    this.bets.set(betId, {
      betId,
      colorId: req.colorId,
      stake: req.stake,
      cellCount,
      outcome,
      revealedCursor: 0,
      cellIndexToOrder,
      done: false,
    });

    const resp: BuyResponse = {
      betId,
      colorId: req.colorId,
      stake: req.stake,
      cellCount,
      outcomeCommit: pseudoHash(`${this.seedStr}:${betId}:commit`),
      nonce: this.nonce,
      balanceAfterDebit: this.balance,
    };
    this.idempotency.set(req.idempotencyKey, resp);
    this.emit({ t: 'balance', amount: this.balance });
    return resp;
  }

  // ---- WS ----
  async connect(): Promise<void> {
    return Promise.resolve();
  }

  send(msg: ClientWsMessage): void {
    if (msg.t === 'tap_cell') this.handleTap(msg.betId, msg.cellIndex);
    else if (msg.t === 'autoplay_ctrl' && msg.action === 'stop') this.haltAutoplay('user');
  }

  private handleTap(betId: string, cellIndex: number): void {
    const bet = this.bets.get(betId);
    if (!bet || bet.done) return;
    const order = bet.cellIndexToOrder[cellIndex];
    if (order == null) return;

    const mult = bet.outcome.cellMults[order] ?? 0;
    const sub = bet.outcome.cellAmounts[order] ?? 0;
    const sparkle = bet.outcome.cellSparkle[order] ?? false;
    bet.revealedCursor++;

    if (sub > 0) {
      this.balance = +(this.balance + sub).toFixed(2);
    }
    this.emit({
      t: 'cell_reveal',
      betId,
      cellIndex,
      subAmount: sub,
      subMult: mult,
      cursor: bet.revealedCursor,
      sparkle,
    });
    if (sub > 0) this.emit({ t: 'balance', amount: this.balance });

    // last cell -> color-complete
    if (bet.revealedCursor >= bet.cellCount) {
      bet.done = true;
      const ccAmt = bet.outcome.colorCompleteAmount;
      this.balance = +(this.balance + ccAmt).toFixed(2);
      this.emit({
        t: 'color_complete',
        betId,
        bonusAmount: ccAmt,
        bonusMult: bet.outcome.colorCompleteMult,
        stardust: bet.outcome.colorCompleteMult,
        mult: bet.outcome.colorCompleteMult,
      });
      this.emit({ t: 'balance', amount: this.balance });

      const pic = this.currentPicture;
      if (pic) {
        pic.completedColors.add(bet.colorId);
        const remaining = pic.K - pic.completedColors.size;
        if (remaining > 0) {
          this.emit({
            t: 'fomo',
            colorsRemaining: remaining,
            projectedBonusTeaser: projectedTeaser(pic.totalStakeAcc, remaining, pic.K),
          });
        }
        this.maybeResolvePicture();
      }
    }
  }

  /** Resolve the picture bonus once ALL colors are bought + completed. */
  private maybeResolvePicture(): void {
    const pic = this.currentPicture;
    if (!pic || pic.pictureResolved) return;
    const allBought = pic.boughtColors.size >= pic.K;
    const allDone = pic.completedColors.size >= pic.K;
    if (!(allBought && allDone)) return;
    pic.pictureResolved = true;
    const outcome = rollPictureOutcome(
      this.seedStr,
      pic.pictureInstanceId,
      pic.totalStakeAcc,
      pic.completedColors.size,
      pic.K,
    );
    if (outcome.amount > 0) {
      this.balance = +(this.balance + outcome.amount).toFixed(2);
    }
    this.emit({
      t: 'picture_complete',
      bonusMult: outcome.effectiveMult,
      bonusAmount: outcome.amount,
      colorsCompleted: outcome.colorsCompleted,
      K: outcome.K,
      tier: outcome.tier,
    });
    this.emit({ t: 'balance', amount: this.balance });
  }

  onMessage(fn: (m: ServerWsMessage) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  // ---- Autoplay (drives buy+tap loops itself) ----
  async startAutoplay(req: AutoplayRequest): Promise<AutoplayResponse> {
    this.autoplayRunId = 'ap-' + randomHex(6);
    const runId = this.autoplayRunId;
    let round = 0;
    let cumulativeLoss = 0;
    const rng = new Rng(`${this.seedStr}:autoplay:${runId}`);

    const stepDelay = 22; // mock pacing; real visual pacing is client-side
    const runRound = async () => {
      if (this.autoplayRunId !== runId) return;
      if (round >= req.rounds) return this.finishAutoplay(runId, 'rounds');
      const stop = this.checkStops(req.stop, cumulativeLoss);
      if (stop) return this.finishAutoplay(runId, stop);

      round++;
      // pick a color from the template that hasn't been bought; if none, new picture
      const pic = this.currentPicture;
      if (!pic) return this.finishAutoplay(runId, 'no-picture');
      const tmpl = req.stakeTemplate.find((t) => !pic.boughtColors.has(t.colorId));
      if (!tmpl) {
        // no more colors to buy in this picture; consider it done
        return this.finishAutoplay(runId, 'picture-complete');
      }
      if (this.balance < tmpl.stake)
        return this.finishAutoplay(runId, 'insufficient');

      const before = this.balance;
      const buy = await this.buyColor({
        colorId: tmpl.colorId,
        stake: tmpl.stake,
        idempotencyKey: 'ap-' + runId + '-' + round + '-' + rng.int(0, 1e9),
      });
      // auto-tap all cells quickly
      const bet = this.bets.get(buy.betId)!;
      for (let i = 0; i < bet.cellCount; i++) this.handleTap(buy.betId, i);

      const after = this.balance;
      const net = after - before; // includes the stake debit already
      const payout = +(net + tmpl.stake).toFixed(2);
      if (payout < tmpl.stake) cumulativeLoss += tmpl.stake - payout;
      const multiple = +(payout / tmpl.stake).toFixed(3);

      this.emit({
        t: 'autoplay_round',
        summary: {
          round,
          colorId: tmpl.colorId,
          stake: tmpl.stake,
          totalPayout: payout,
          multiple,
          balance: after,
        },
      });

      // stop conditions reacting to this round
      if (req.stop.onSingleWinExceeds != null && payout >= req.stop.onSingleWinExceeds)
        return this.finishAutoplay(runId, 'single-win');
      if (req.stop.stopOnTailX != null && multiple >= req.stop.stopOnTailX)
        return this.finishAutoplay(runId, 'tail');

      this.autoplayTimer = setTimeout(runRound, stepDelay);
    };
    void runRound();
    return { autoplayRunId: runId, status: 'running' };
  }

  private checkStops(stop: AutoplayStop, cumLoss: number): string | null {
    if (stop.onLossExceeds != null && cumLoss >= stop.onLossExceeds) return 'loss-limit';
    if (stop.onBalanceBelow != null && this.balance <= stop.onBalanceBelow)
      return 'balance-below';
    if (stop.onBalanceAbove != null && this.balance >= stop.onBalanceAbove)
      return 'balance-above';
    return null;
  }

  private finishAutoplay(runId: string, reason: string): AutoplayResponse {
    if (this.autoplayTimer) clearTimeout(this.autoplayTimer);
    this.autoplayTimer = null;
    if (this.autoplayRunId === runId) this.autoplayRunId = null;
    this.emit({ t: 'autoplay_done', autoplayRunId: runId, reason });
    return { autoplayRunId: runId, status: 'done' };
  }

  private haltAutoplay(reason: string): void {
    if (this.autoplayRunId) this.finishAutoplay(this.autoplayRunId, reason);
  }

  async stopAutoplay(_req: AutoplayStopRequest): Promise<AutoplayResponse> {
    const id = this.autoplayRunId ?? _req.autoplayRunId;
    this.haltAutoplay('user');
    return { autoplayRunId: id, status: 'stopped' };
  }

  // ---- Fairness ----
  async rotateSeed(): Promise<RotateSeedResponse> {
    const revealed = this.serverSeed;
    this.serverSeed = randomHex(64);
    this.serverSeedHash = pseudoHash(this.serverSeed);
    this.nonce = 0;
    return { revealedServerSeed: revealed, newServerSeedHash: this.serverSeedHash };
  }

  async getFairness(betId: string): Promise<FairnessResponse> {
    const bet = this.bets.get(betId);
    return {
      serverSeed: this.serverSeed,
      clientSeed: this.clientSeed,
      nonce: this.nonce,
      mathVersion: MATH_VERSION,
      draws: bet ? bet.outcome.draws : [],
      computedPayouts: bet
        ? [...bet.outcome.cellAmounts, bet.outcome.colorCompleteAmount]
        : [],
    };
  }

  dispose(): void {
    if (this.autoplayTimer) clearTimeout(this.autoplayTimer);
    this.listeners.clear();
  }

  // expose split for debug/tooltips
  get splitInfo() {
    return SPLIT;
  }
}
